import { PERMISSIONS } from '@ibe/constants';
import {
  Button,
  ConfirmDialog,
  ErrorState,
  Field,
  FieldLabel,
  FieldSelect,
  LoadingState,
  StatusBadge,
  toneForStatus,
} from '@ibe/ui';
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { PrimaryCell, RowActions } from '../../../components/resource-list/cells.js';
import { ResourceListPage } from '../../../components/resource-list/resource-list-page.js';
import { errorTextFor } from '../../../lib/api/error-message.js';
import { type ColumnDescriptor } from '../../../lib/resource-list/columns.js';
import { type FilterDescriptor } from '../../../lib/resource-list/filters.js';
import { useSoftDeleteTenantAdmin } from '../api/mutations.js';
import { tenantAdminEndpoints } from '../api/endpoints.js';
import {
  TENANT_ADMIN_STATUS_OPTIONS,
  tenantAdminRow,
  type TenantAdminRow,
} from '../api/schemas.js';
import { useTenantOptions } from '../api/queries.js';

/**
 * The tenant administrators list for one tenant.
 *
 * Search, sort, pagination, filtering and the three non-data states all come
 * from `ResourceListPage`. What is here is the descriptor: which columns
 * exist, which backend fields each one reads, and what a row can be done to.
 *
 * The tenant picker sits outside `ResourceListPage` because it is the scope of
 * the whole screen, not a filter on the resource: the list is meaningless
 * without a tenant, and the new-administrator link must carry the chosen one.
 */

const filters: readonly FilterDescriptor[] = [
  {
    id: 'status',
    label: 'Status',
    param: 'status',
    kind: 'select',
    options: TENANT_ADMIN_STATUS_OPTIONS,
  },
];

/** A UTC calendar date. The value is an ISO instant; the day is what reads. */
function formatDate(value: string | undefined): string {
  if (value === undefined) return '';
  return new Date(value).toLocaleDateString('en-AU', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** A display name from the first/last name, falling back to the email. */
function displayName(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  email: string | undefined,
): string {
  const name = [firstName, lastName]
    .filter((part): part is string => part !== null && part !== undefined && part !== '')
    .join(' ');
  return name === '' ? (email ?? '') : name;
}

function useTenantParam(): [string, (next: string) => void] {
  const [params, setParams] = useSearchParams();
  const tenantId = params.get('tenantId') ?? '';

  const setTenantId = (next: string): void => {
    const updated = new URLSearchParams(params);
    if (next === '') {
      updated.delete('tenantId');
    } else {
      updated.set('tenantId', next);
    }
    updated.delete('page');
    setParams(updated, { replace: true });
  };

  return [tenantId, setTenantId];
}

function TenantField({
  tenantId,
  onValueChange,
  options,
}: {
  readonly tenantId: string;
  readonly onValueChange: (value: string) => void;
  readonly options: readonly { readonly value: string; readonly label: string }[];
}): React.JSX.Element {
  return (
    <div className="min-w-44">
      <Field>
        <FieldLabel>Tenant</FieldLabel>
        <FieldSelect
          value={tenantId}
          onValueChange={onValueChange}
          options={options}
          placeholder="Select a tenant"
          searchable
        />
      </Field>
    </div>
  );
}

export const tenantAdminColumns: readonly ColumnDescriptor<TenantAdminRow>[] = [
  {
    id: 'firstName',
    header: 'Name',
    sortable: true,
    alwaysVisible: true,
    fields: ['firstName', 'lastName', 'email'],
    cell: (row) => (
      <PrimaryCell
        label={displayName(row.firstName, row.lastName, row.email)}
        to={`/tenant-admins/${row.id}?tenantId=${encodeURIComponent(row.tenantId)}`}
        secondary={row.email ?? null}
      />
    ),
  },
  {
    id: 'email',
    header: 'Email',
    sortable: true,
    fields: ['email'],
    cell: (row) => row.email ?? '',
  },
  {
    id: 'status',
    header: 'Status',
    sortable: true,
    fields: ['status'],
    cell: (row) =>
      row.status === undefined ? null : (
        <StatusBadge tone={toneForStatus(row.status)}>{row.status}</StatusBadge>
      ),
  },
  {
    id: 'createdAt',
    header: 'Created',
    sortable: true,
    fields: ['createdAt'],
    defaultVisible: false,
    cell: (row) => formatDate(row.createdAt),
  },
  {
    id: 'actions',
    header: '',
    headerLabel: 'Actions',
    align: 'right',
    alwaysVisible: true,
    fields: ['firstName', 'lastName', 'email', 'tenantId'],
    cell: (row: TenantAdminRow): React.JSX.Element => <TenantAdminRowActions row={row} />,
  },
];

/**
 * The per-row controls, as a component rather than inline markup.
 *
 * Deleting needs a mutation, a confirmation and a permission check, and a
 * column descriptor's `cell` is a plain function — it cannot hold hooks. This
 * is the smallest thing that can.
 */
function TenantAdminRowActions({ row }: { readonly row: TenantAdminRow }): React.JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const softDelete = useSoftDeleteTenantAdmin();
  const label = displayName(row.firstName, row.lastName, row.email);

  return (
    <>
      <div className="flex flex-nowrap items-center justify-end gap-1">
        <RowActions
          actions={[
            {
              label: `View ${label}`,
              icon: Eye,
              to: `/tenant-admins/${row.id}?tenantId=${encodeURIComponent(row.tenantId)}`,
            },
            {
              label: `Edit ${label}`,
              icon: Pencil,
              to: `/tenant-admins/${row.id}/edit?tenantId=${encodeURIComponent(row.tenantId)}`,
            },
          ]}
        />
        {/* Offered only to someone who may actually delete. A control that
            always answers 403 teaches people to distrust every other button. */}
        <PermissionGuard permission={PERMISSIONS.TENANT_USER_MANAGE}>
          <RowActions
            actions={[
              {
                label: `Delete ${label}`,
                icon: Trash2,
                destructive: true,
                onClick: () => {
                  setConfirming(true);
                },
              },
            ]}
          />
        </PermissionGuard>
      </div>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Delete tenant administrator"
        description={`${label} will be removed from this tenant. Their audit trail and historical actions are retained.`}
        confirmLabel="Delete"
        destructive
        loading={softDelete.isPending}
        onConfirm={() => {
          softDelete.mutate(
            { userId: row.id, tenantId: row.tenantId },
            {
              onSuccess: () => {
                setConfirming(false);
              },
            },
          );
        }}
      />
    </>
  );
}

export function TenantAdminListPage(): React.JSX.Element {
  const [tenantId, setTenantId] = useTenantParam();
  const tenants = useTenantOptions();

  useEffect(() => {
    if (tenantId === '' && tenants.data !== undefined && tenants.data.length > 0) {
      const first = tenants.data[0];
      if (first !== undefined) {
        setTenantId(first.id);
      }
    }
  }, [tenantId, tenants.data, setTenantId]);

  if (tenants.isPending) {
    return <LoadingState label="Loading tenants" />;
  }

  if (tenants.error !== null) {
    return (
      <ErrorState
        description={errorTextFor(tenants.error) ?? 'The tenant list could not be loaded.'}
      />
    );
  }

  const tenantOptions = tenants.data.map((tenant) => ({
    value: tenant.id,
    label: tenant.name,
  }));

  const newAdministrator = (
    <PermissionGuard permission={PERMISSIONS.TENANT_USER_MANAGE}>
      <Button asChild>
        <Link
          to={
            tenantId === ''
              ? '/tenant-admins/new'
              : `/tenant-admins/new?tenantId=${encodeURIComponent(tenantId)}`
          }
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          New administrator
        </Link>
      </Button>
    </PermissionGuard>
  );

  return (
    <section className="space-y-section">
      {tenantId === '' ? (
        <div className="space-y-4">
          <header className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1">
              <h1 className="text-page-title font-semibold">Tenant administrators</h1>
              <p className="text-body text-muted-foreground">
                The owners who can sign in and manage each tenant.
              </p>
            </div>
            {newAdministrator}
          </header>

          <div className="flex flex-wrap items-end gap-4">
            <TenantField tenantId={tenantId} onValueChange={setTenantId} options={tenantOptions} />
          </div>

          <p className="text-sm text-muted-foreground">
            Select a tenant to see its administrators.
          </p>
        </div>
      ) : (
        <ResourceListPage
          title="Tenant administrators"
          description="The owners who can sign in and manage each tenant."
          path={tenantAdminEndpoints.list}
          resource="tenantAdmins"
          schema={tenantAdminRow}
          columns={tenantAdminColumns}
          filters={filters}
          rowKey={(row) => row.id}
          permission={PERMISSIONS.TENANT_READ}
          defaultSort={{ columnId: 'firstName', direction: 'asc' }}
          scope={{ tenantId }}
          actions={newAdministrator}
          filterPrefix={
            <TenantField tenantId={tenantId} onValueChange={setTenantId} options={tenantOptions} />
          }
        />
      )}
    </section>
  );
}
